import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as IconTrash } from 'bootstrap-icons/icons/trash.svg'
import { ReactComponent as IconArrowClockwise } from 'bootstrap-icons/icons/arrow-clockwise.svg'
import { ReactComponent as IconLock } from 'bootstrap-icons/icons/file-earmark-lock2-fill.svg'
import { v4 as uuid } from "uuid";


import parseDate from 'lib/parseDate'
import recordEditWindowMs from 'lib/recordEditWindowMs'
import useUser from "hooks/useUser";
import useMutation from "hooks/useMutation";
import Editor from "../../../EHR/Editor/Editor";
import Calendar from '../Home/Calendar'
import useStore from '../useStore'

function Record(props) {
  const user = useUser();
  const { t } = useTranslation();
  const [state, setState] = useState();
  const [editable, setEditable] = useState()
  const [loading, setLoading] = useState()
  const ticker = useRef(null)
  const store = useStore(state => ({
    resource: state.resource,
    consumer: state.consumer,
    records: state.records || [],
    update: state.update,
  }))
  const [mutate, mutation] = useMutation(window._l(`/records`).substr(1));

  const onChange = payload => {
    const record = { ...state, ...payload }
    setState(record)
    clearTimeout(ticker.current)
    setLoading(true)
    ticker.current = setTimeout(() => {
      mutate(record, {
        // Bug real (auditoría 2026-08-14): setLoading(false) se llamaba acá
        // (antes de mutate), no en onSuccess/onError — el spinner y el
        // disabled de los botones de borrar/restaurar se apagaban apenas
        // arrancaba el guardado, no cuando terminaba. Sin onError, un
        // guardado fallido no avisaba nada y el profesional creía que había
        // quedado guardado.
        onSuccess: data => {
          setLoading(false)
          setState(state => {
            const payload = { ...data, ...state }
            let records = [...store.records]
            const index = records.findIndex(r => r.id === payload.id)
            if (index >= 0) {
              records[index] = payload
            } else {
              records = [...records, payload]
            }
            store.update({ records })

            return payload
          })
        },
        onError: error => {
          setLoading(false)
          window.notify({
            message: error.message,
            type: "danger",
            title: "Error",
          })
        }
      })
    }, 2000)
  }

  const toggle = () => {
    const deleted = !(state.deleted ?? false)
    if (state.id && deleted && !window.confirm(t('ehr:remove_record', 'Confirmas que queres borrar el registro', { context: state.type }))) {
      return
    }
    onChange({ deleted })
  };

  useEffect(() => {
    if (user.state?.uid && store.consumer?.id) {
      const payload = {
        date: new Date().toLocaleDateString('fr-ca'),
        type: "records",
        consumer: { id: store.consumer?.id, label: store.consumer?.label },
        createdAt: Date.now(),
        createdBy: user.state.uid,
        createdByName: store.resource?.label,
        createdByResourceID: store.resource?.id,
        ...props.state
      }
      if (!payload.id) {
        payload._id = uuid()
      }
      setState(payload);
    }
  }, [props.state, user.state.uid, store.consumer, store.resource]);

  useEffect(() => {
    // Ventana unificada con EHR V2 (Consultation.js, lib/recordEditWindowMs) —
    // antes esta vista (V1) usaba 24hs y V2 usaba 48hs para el MISMO registro,
    // según por dónde entrara el profesional.
    const editable = state?.createdBy === user.state.uid && Date.now() - state?.createdAt <= recordEditWindowMs;
    setEditable(editable)
  }, [state?.createdBy, state?.createdAt, user.state.uid])

  if (!user.state.uid) return null;

  if (!state) return (
    <div className="mvh-100-1 d-flex align-items-center justify-content-center">
      <div className="spinner-border text-primary">
        <span className="visually-hidden">{t("loading")}</span>
      </div>
    </div>
  )

  return (
    <div className="rounded bg-white position-relative">
      {loading && <div className="loading loading-bottom loading-sm" />}

      <div>
        <div>
          <Editor
            disabled={!editable}
            id={state.id || state._id}
            value={state.content}
            placeholder={t("ehr:record_placeholder", "Escriba aqui la evolucion")}
            onEditorChange={content => onChange({ content })}
          />
        </div>

        <div className="d-flex align-items-center">
          {loading && (
            <div className="spinner-border-sm spinner-border ms-2 text-primary"></div>
          )}
          <Calendar
            inline
            disabled={!editable}
            label={t('date', 'fecha')}
            value={{
              value: state.date,
              label: parseDate(state.date)?.toLocaleDateString() || t('undefined', 'Indefinida', { context: 'female' })
            }}
            onChange={date => onChange({ date: date?.toLocaleDateString('fr-ca') || "" })}
          />
          {state.id ? (
            state.deleted && editable && !loading ? (
              // Fix responsive (2026-08-14): p-0 sobre un ícono de 16px
              // dejaba el hit-target en 16x16 — se usa p-2 (mismo patrón que
              // el resto del PR) sin cambiar el tamaño visual del ícono.
              <button disabled={loading && !state.id} data-tooltip-id="app-tooltip" data-tooltip-content={t('restore', 'Restaurar')} className="btn btn-sm p-2" onClick={toggle}>
                <IconArrowClockwise className="text-success" width="16" height="16" />
              </button>
            ) : editable && !loading ? (
              <button disabled={loading && !state.id} className="btn btn-sm p-2 text-danger" onClick={toggle}>
                <IconTrash className="text-danger" width="16" height="16" />
              </button>
            ) : !editable && (
              <small
                className="text-secondary lh-1"
                data-tooltip-id="app-tooltip"
                data-tooltip-content={t(
                  "ehr:editLockedTip",
                  "Los registros solo se pueden editar hasta 48hs despes de la creacion, y por el mismo que lo haya creado"
                )}
              >
                <IconLock width="16" height="16" /> {t("locked", "Bloqueado", { context: 'record' })}
              </small>
            )
          ) : props.onRemove && (
            <button className="btn btn-sm p-2 text-danger" onClick={() => props.onRemove(state._id)}>
              <IconTrash className="text-danger" width="16" height="16" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Record;
